// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Redline PDF/DOCX utilities — presigned URL access for annotated artifacts.
 *
 * Dataset-scoped helpers are the default — they read artifacts written by the
 * redline node in dataset mode at
 * `{doc_prefix}/datasets/{agent_id}/{dataset_key}/...`.
 *
 * The single pipeline-scoped helper (`getPipelineRedlineDocxUrl`) is reserved
 * for the NegotiationRenderer, which consumes the merged DOCX produced by the
 * contract-negotiation pipeline running the redline node in pipeline mode.
 */
import type { NodeApiClient } from '@/app-shell/services'

interface ViewUrlResponse {
  url: string
  expiresIn: number
}

interface DocxResponse {
  downloadUrl: string
  filename: string
}

/**
 * Which annotated artifact to fetch.
 *
 * Annotated artifacts live beside the report they annotate, under that run's
 * prefix, so `run` is how one is identified. Pass the run the payload fetch
 * resolved — that is the run whose findings are on screen. Omitting it falls back
 * to the newest run at this version, which is only right when nothing has
 * resolved a specific one.
 */
interface RedlineOptions {
  version?: number
  run?: number
}

function redlineParams(
  agentId: string,
  datasetKey: string,
  options?: RedlineOptions,
): URLSearchParams {
  const params = new URLSearchParams({ agent_id: agentId, dataset_key: datasetKey })
  if (options?.version !== undefined) params.set('version', String(options.version))
  if (options?.run !== undefined) params.set('run', String(options.run))
  return params
}

/**
 * Get a presigned URL for the dataset-scoped annotated PDF.
 * Calls `/redline?agent_id=...&dataset_key=...`.
 */
export async function getRedlinePdfUrl(
  apiClient: NodeApiClient,
  orgId: string,
  wsId: string,
  documentId: string,
  agentId: string,
  datasetKey: string,
  options?: RedlineOptions & { download?: boolean },
): Promise<string> {
  const params = redlineParams(agentId, datasetKey, options)
  if (options?.download) params.set('download', 'true')
  const url = `/org/${orgId}/ws/${wsId}/contract-review/documents/${documentId}/redline?${params}`
  const res = await apiClient.get<ViewUrlResponse>(url)
  return res.data.url
}

/**
 * Get a presigned URL for the dataset-scoped annotated DOCX (Word comments).
 * Calls `/redline-docx?agent_id=...&dataset_key=...`.
 */
export async function getRedlineDocxUrl(
  apiClient: NodeApiClient,
  orgId: string,
  wsId: string,
  documentId: string,
  agentId: string,
  datasetKey: string,
  options?: RedlineOptions,
): Promise<DocxResponse> {
  const params = redlineParams(agentId, datasetKey, options)
  const url = `/org/${orgId}/ws/${wsId}/contract-review/documents/${documentId}/redline-docx?${params}`
  const res = await apiClient.get<DocxResponse>(url)
  return res.data
}

/**
 * Get a presigned URL for the pipeline-scoped annotated DOCX.
 *
 * Only the NegotiationRenderer calls this — the contract-negotiation pipeline
 * runs the redline node in pipeline mode and emits a single merged DOCX at
 * `{doc_prefix}/pipelines/{pipeline_id}/annotated.docx`. Every other redline
 * fetch in the app uses the dataset-scoped helpers above.
 */
export async function getPipelineRedlineDocxUrl(
  apiClient: NodeApiClient,
  orgId: string,
  wsId: string,
  documentId: string,
  pipelineId: string,
  version: number,
): Promise<DocxResponse> {
  const params = new URLSearchParams({
    pipeline_id: pipelineId,
    version: String(version),
  })
  const url = `/org/${orgId}/ws/${wsId}/contract-review/documents/${documentId}/pipeline-redline-docx?${params}`
  const res = await apiClient.get<DocxResponse>(url)
  return res.data
}

/** Trigger a browser download from a URL */
export function downloadFromUrl(url: string, filename: string): void {
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
